import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import axios from 'axios';
import { CSVLink } from 'react-csv';


const API_BASE_URL = 'https://kigaawater.inkenya.co.ke/api';
// ──────────────────────────────────────────────────────────────
// TEMPLATE COLUMN DEFINITIONS
// ──────────────────────────────────────────────────────────────
const CUSTOMER_HEADERS = [
  { label: "First Name", key: "first_name" },
  { label: "Last Name", key: "last_name" },
  { label: "Phone Number", key: "phone_number" },
  { label: "Installation Address", key: "installation_address" },
  { label: "Block ID", key: "block_id" },
  { label: "Account Number", key: "account_number" },
  { label: "Is Active (true or false)", key: "is_active" },
  { label: "Monthly Rate", key: "monthly_rate" },
  { label: "Next Billing Date (YYYY-MM-DD)", key: "next_billing_date" },
];

const TRANSACTION_HEADERS = [
  { label: "Account Number", key: "account_number" },
  { label: "Amount (Use -ve for Payment/Credit Note, +ve for Invoice)", key: "amount" },
  { label: "Transaction Type (Must be: INVOICE, PAYMENT, or CREDIT_NOTE)", key: "transaction_type" },
  { label: "Reference Number", key: "reference_number" },
  { label: "Payment Method (e.g., CASH, MPESA, BANK)", key: "payment_method" },
  { label: "Transaction Date (YYYY-MM-DD)", key: "transaction_date" },
];



// ──────────────────────────────────────────────────────────────
// BULK UPLOAD MODAL
// ──────────────────────────────────────────────────────────────
const BulkUploadModal = ({
  isOpen,
  onClose,
  type,
  headers,
  instructions,
  onUpload,
  styles,
}) => {
  const [file, setFile] = useState(null);
  const fileName = `${type.toLowerCase().replace(' ', '_')}_template.csv`;

  if (!isOpen) return null;

  const handleFileChange = e => setFile(e.target.files[0]);

  const handleUploadClick = () => {
    if (file) {
      onUpload(file);
      setFile(null);
    } else {
      alert('Please select a file to upload.');
    }
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.bulkModalContent}>
        <h3 style={styles.modalHeader}>Bulk Upload {type}</h3>
        <p style={styles.instructionsText}>
          <strong>Instructions:</strong> {instructions}
        </p>

        <div style={styles.downloadSection}>
          <span style={styles.downloadText}>1. Download Template:</span>
          <CSVLink
            data={[]}
            headers={headers}
            filename={fileName}
            style={styles.downloadButton}
          >
            Download Template (.csv)
          </CSVLink>
        </div>

        <div style={styles.uploadSection}>
          <span style={styles.downloadText}>2. Upload Filled File:</span>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={styles.fileInput}
          />
        </div>

        <div style={styles.modalFooter}>
          <button
            style={{
              ...styles.submitButton,
              backgroundColor: '#6c757d',
              marginRight: '10px',
              marginTop: 0,
            }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            style={{
              ...styles.submitButton,
              backgroundColor: file ? '#007bff' : '#adb5bd',
              marginTop: 0,
            }}
            onClick={handleUploadClick}
            disabled={!file}
          >
            Upload
          </button>
        </div>

        <button onClick={onClose} style={styles.closeButtonSmall}>
          ×
        </button>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ──────────────────────────────────────────────────────────────
function CustomerBalances() {
  // ────── State ──────
  const [balances, setBalances] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [transactionFilter, setTransactionFilter] = useState('All');
  const [reportStatus, setReportStatus] = useState(null);
  const [viewMode, setViewMode] = useState('history');
  const [selectedTx, setSelectedTx] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [monthlyBillingStatus, setMonthlyBillingStatus] = useState(null); // null | 'running' | 'success' | 'error'

  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);

  const [minBalanceThreshold, setMinBalanceThreshold] = useState(0);
  const [balanceFilterType, setBalanceFilterType] = useState('NONE');

  const [blockFilter, setBlockFilter] = useState('All');
const [membershipFilter, setMembershipFilter] = useState('All');
const [extraFiltersOpen, setExtraFiltersOpen] = useState(false);

  // NEW: Create Invoice Modal
  const [isCreateInvoiceOpen, setIsCreateInvoiceOpen] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    amount: '',
    description: 'Monthly Water Bill',
    referenceNumber: '',
    date: new Date().toISOString().split('T')[0],
  });

  // ────── Helpers ──────
  const renderStatus = useCallback(
    (status, type) => {
      const base = {
        padding: '12px',
        borderRadius: '8px',
        fontWeight: '600',
        marginTop: '15px',
        display: 'flex',
        alignItems: 'center',
        minHeight: '40px',
      };
      let style = {};
      let prefix = '';

      switch (type) {
        case 'success':
          style = { ...base, backgroundColor: '#d4edda', color: '#155724' };
          prefix = 'Success';
          break;
        case 'error':
          style = { ...base, backgroundColor: '#f8d7da', color: '#721c24' };
          prefix = 'Error';
          break;
        case 'loading':
          style = { ...base, backgroundColor: '#cce5ff', color: '#004085' };
          prefix = 'Loading';
          break;
        default:
          style = { ...base, backgroundColor: '#e2e3e5', color: '#383d41' };
          prefix = 'Info';
          break;
      }

      return (
        <div style={style} data-type={type}>
          <span style={{ marginRight: '10px' }}>{prefix}</span>
          {status}
        </div>
      );
    },
    []
  );

  const fetchBalances = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/customer-balances`);
      setBalances(data);
      return data;
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setReportStatus(renderStatus(`Error fetching balances: ${msg}`, 'error'));
      return [];
    }
  }, [renderStatus]);

  // ────── Effects ──────
  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  useEffect(() => {
    const fetchBlocks = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/blocks`);
        setBlocks(data);
      } catch (err) {
        const msg = err.response?.data?.error || err.message;
        setReportStatus(renderStatus(`Error fetching blocks: ${msg}`, 'error'));
      }
    };
    fetchBlocks();
  }, [renderStatus]);

  // ────── Handlers ──────

const handleRunMonthlyBilling = async () => {
  if (!window.confirm('Run monthly billing for ALL active customers now?\n\nThis will create "Monthly Water Bill" invoices for everyone with monthly_rate > 0.')) {
    return;
  }

  setMonthlyBillingStatus('running');

  try {
    const response = await axios.post(`${API_BASE_URL}/run-monthly-billing`, {}, { withCredentials: true });

    setMonthlyBillingStatus('success');
    setReportStatus(renderStatus(response.data.message || 'Monthly billing completed!', 'success'));
    
    // Refresh balances after 2 seconds
    setTimeout(() => {
      fetchBalances();
    }, 2000);
  } catch (err) {
    const msg = err.response?.data?.message || err.response?.data?.error || err.message;
    if (msg.includes('Not the last day')) {
      setMonthlyBillingStatus('success'); // still show green
      setReportStatus(renderStatus('Note: Today is not the last day of the month, but billing was forced.', 'info'));
    } else {
      setMonthlyBillingStatus('error');
      setReportStatus(renderStatus(`Monthly billing failed: ${msg}`, 'error'));
    }
  }
};



  const handleAccountSelect = async accountNumber => {
    setSelectedAccount(accountNumber);
    setCustomerData(null);
    setEditingCustomer(null);
    setReportStatus(null);
    setViewMode('history');

    try {
      const { data } = await axios.get(`${API_BASE_URL}/customer-data`, {
        params: { accountNumber },
      });

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth(); // 0-based
const lastDayDate = new Date(currentYear, currentMonth + 1, 0);

const yyyy = lastDayDate.getFullYear();
const mm = String(lastDayDate.getMonth() + 1).padStart(2, '0');
const dd = String(lastDayDate.getDate()).padStart(2, '0');
const lastDayOfMonth = `${yyyy}-${mm}-${dd}`;

const lastDayDateObj = new Date(lastDayOfMonth);
const storedDateObj = data.next_billing_date ? new Date(data.next_billing_date) : null;

      const nextBillingDate = data.next_billing_date && new Date(data.next_billing_date) >= new Date(lastDayOfMonth)
        ? data.next_billing_date
        : lastDayOfMonth;

      setCustomerData(data);
      setEditingCustomer({
        ...data,
        block_id: blocks.find(b => b.block_name === data.block_name)?.block_id ?? '',
        monthly_rate: parseFloat(data.monthly_rate) || 0,
        next_billing_date: nextBillingDate,
      });
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setReportStatus(renderStatus(`Error fetching account data: ${msg}`, 'error'));
    }
  };

  const handleUpdateCustomer = async () => {
    try {
      await axios.put(`${API_BASE_URL}/update-customer`, {
        userId: editingCustomer.user_id,
        first_name: editingCustomer.first_name,
        last_name: editingCustomer.last_name,
        phone_number: editingCustomer.phone_number,
        accountId: editingCustomer.account_id,
        installation_address: editingCustomer.installation_address,
        block_id: editingCustomer.block_id,
        is_active: editingCustomer.is_active,
        monthly_rate: parseFloat(editingCustomer.monthly_rate),
        next_billing_date: editingCustomer.next_billing_date,
      });

      const updated = {
        ...customerData,
        ...editingCustomer,
        block_name: blocks.find(b => b.block_id === editingCustomer.block_id)?.block_name ?? '',
        next_billing_date: editingCustomer.next_billing_date,
      };
      setCustomerData(updated);
      setReportStatus(renderStatus('Customer details updated successfully.', 'success'));
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setReportStatus(renderStatus(`Error updating customer: ${msg}`, 'error'));
    }
  };

  // ────── Bulk Upload Handlers ──────
  const handleCustomerBulkUpload = async file => {
    const form = new FormData();
    form.append('file', file);

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/bulk-upload-customers`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setReportStatus(
        renderStatus(
          `Customer Upload: ${data.message}. Refreshing data...`,
          'success'
        )
      );
      await fetchBalances();
      setIsCustomerModalOpen(false);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setReportStatus(renderStatus(`Customer Upload Failed: ${msg}`, 'error'));
    }
  };

  const handleTransactionBulkUpload = async file => {
    const form = new FormData();
    form.append('file', file);

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/bulk-upload-transactions`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setReportStatus(
        renderStatus(
          `Transaction Upload: ${data.message}. Refreshing data...`,
          'success'
        )
      );
      await fetchBalances();
      if (selectedAccount) await handleAccountSelect(selectedAccount);
      setIsTransactionModalOpen(false);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setReportStatus(
        renderStatus(`Transaction Upload Failed: ${msg}`, 'error')
      );
    }
  };

  // ────── NEW: Create Invoice Handler ──────
const handleCreateInvoice = async (e) => {
  e.preventDefault();

  if (!invoiceForm.amount || !invoiceForm.billFromMonth) {
    setReportStatus(renderStatus('Amount and Month are required.', 'error'));
    return;
  }

  try {
    const payload = {
      accountNumber: selectedAccount,
      amount: parseFloat(invoiceForm.amount),
      description: invoiceForm.description || 'Manual Backdated Billing',
      referenceNumber: invoiceForm.referenceNumber || '',
      billFromMonth: invoiceForm.billFromMonth, // NEW
    };

    const { data } = await axios.post(
      `${API_BASE_URL}/create-backdated-invoices`,
      payload
    );

    setReportStatus(renderStatus(data.message, 'success'));
    setIsCreateInvoiceOpen(false);
      setInvoiceForm({
        amount: '',
        description: '',
        referenceNumber: '',
        date: new Date().toISOString().split('T')[0],
      });

    await handleAccountSelect(selectedAccount);

  } catch (err) {
    const msg = err.response?.data?.error || err.message;
    setReportStatus(renderStatus(`Failed: ${msg}`, 'error'));
  }
};

  // ────── Filtered balances (Frontend UI) ──────
  const filteredBalances = useMemo(() => {
    let list = balances;

    if (statusFilter !== 'All') {
      const active = statusFilter === 'Active';
      list = list.filter(a => a.is_active === active);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        a =>
          a.account_number.toLowerCase().includes(q) ||
          `${a.first_name} ${a.last_name}`.toLowerCase().includes(q)
      );
    }

    if (balanceFilterType !== 'NONE') {
      const threshold = parseFloat(minBalanceThreshold) || 0;
      if (!isNaN(threshold)) {
        list = list.filter(a => {
          const bal = parseFloat(a.current_balance);
          switch (balanceFilterType) {
            case 'GT':  return bal > threshold;
            case 'GTE': return bal >= threshold;
            case 'LT':  return bal < threshold;
            case 'LTE': return bal <= threshold;
            default:    return true;
          }
        });
      }
    }

    // NEW: Block filter
    if (blockFilter !== 'All') {
      list = list.filter(a => a.block_id === Number(blockFilter));
    }
if (membershipFilter !== 'All') {
        const prefix = membershipFilter === 'Member' ? 'M' : 'N';
        list = list.filter(a => a.account_number.startsWith(prefix));
    }
    return list;
  }, [
    balances,
    searchQuery,
    statusFilter,
    minBalanceThreshold,
    balanceFilterType,
    blockFilter,
membershipFilter,
  ]);

  // ────── Print PDF (ALL FILTERS) ──────
  const handlePrintBalances = async () => {
    setReportStatus(renderStatus('Generating detailed PDF report...', 'loading'));

    try {
      const params = new URLSearchParams();

      // 1. Status Filter
      if (statusFilter !== 'All') {
        params.append('status', statusFilter);
      }

      // 2. Search Query
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      // 3. Balance Filter
      if (balanceFilterType !== 'NONE') {
        const threshold = parseFloat(minBalanceThreshold);
        if (!isNaN(threshold)) {
          params.append('minBalance', threshold.toString());
          params.append('filterType', balanceFilterType);
        }
      }

      // NEW: Block Filter
      if (blockFilter !== 'All') {
        params.append('blockId', blockFilter);
      }

if (membershipFilter !== 'All') {
    params.append('membership', membershipFilter);
}

      const queryString = params.toString();
      const url = `${API_BASE_URL}/balance-report-pdf${queryString ? `?${queryString}` : ''}`;

      const { data } = await axios.get(url, { responseType: 'blob' });

      const blobUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');

      setReportStatus(
        renderStatus('Detailed balance report generated and opened for printing.', 'success')
      );
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setReportStatus(renderStatus(`Error generating report: ${msg}`, 'error'));
    }
  };

  // ────── Transaction ledger & statuses (unchanged) ──────
  const transactionStatuses = useMemo(() => {
    if (!customerData?.transactions || !customerData?.allocations)
      return { invoices: [], payments: [], creditNotes: [], ledger: [] };

    const txMap = new Map(
      customerData.transactions.map(t => [t.transaction_id, { ...t, amount: +t.amount }])
    );

    const invoices = customerData.transactions
      .filter(t => t.transaction_type === 'INVOICE' && +t.amount > 0)
      .map(t => {
        const allocs = customerData.allocations
          .filter(a => a.invoice_transaction_id === t.transaction_id)
          .map(a => ({
            payment: txMap.get(a.payment_transaction_id),
            amount: +a.amount,
          }));
        const allocated = allocs.reduce((s, a) => s + a.amount, 0);
        const remaining = +t.amount - allocated;
        const status =
          remaining === 0
            ? 'Fully Paid'
            : remaining === +t.amount
            ? 'Unpaid'
            : 'Partially Paid';
        return { tx: { ...t, amount: +t.amount }, remaining, applied: allocs, status };
      });

    const payments = customerData.transactions
      .filter(t => t.transaction_type === 'PAYMENT' && +t.amount < 0)
      .map(t => {
        const utilized = customerData.allocations
          .filter(a => a.payment_transaction_id === t.transaction_id)
          .reduce((s, a) => s + +a.amount, 0);
        const abs = Math.abs(+t.amount);
        const status =
          utilized >= abs
            ? 'Fully Utilized'
            : utilized > 0
            ? 'Partially Utilized'
            : 'Not Utilized';
        return { tx: { ...t, amount: +t.amount }, utilized, status };
      });

    const creditNotes = customerData.transactions
      .filter(t => t.transaction_type === 'CREDIT_NOTE' && +t.amount < 0)
      .map(t => {
        const utilized = customerData.allocations
          .filter(a => a.payment_transaction_id === t.transaction_id)
          .reduce((s, a) => s + +a.amount, 0);
        const abs = Math.abs(+t.amount);
        const status =
          utilized >= abs
            ? 'Fully Utilized'
            : utilized > 0
            ? 'Partially Utilized'
            : 'Not Utilized';
        return { tx: { ...t, amount: +t.amount }, utilized, status };
      });

    let running = 0;
    const ledger = customerData.transactions
      .filter(t => transactionFilter === 'All' || t.transaction_type === transactionFilter)
      .sort((a, b) => new Date(a.transaction_date) - new Date(b.transaction_date))
      .map(t => {
        running += +t.amount;
        const status =
          t.transaction_type === 'INVOICE'
            ? invoices.find(i => i.tx.transaction_id === t.transaction_id)?.status ?? 'Unpaid'
            : t.transaction_type === 'PAYMENT'
            ? payments.find(p => p.tx.transaction_id === t.transaction_id)?.status ?? 'Not Utilized'
            : creditNotes.find(c => c.tx.transaction_id === t.transaction_id)?.status ?? 'Not Utilized';
        return {
          ...t,
          running_balance: running,
          type: t.transaction_type,
          status,
          payment_method: t.payment_method || 'Unknown',
        };
      })
      .reverse();

    return { invoices, payments, creditNotes, ledger };
  }, [customerData, transactionFilter]);

  // ────── Print transaction history (unchanged) ──────
  const handlePrintHistory = () => {
    if (!customerData) {
      setReportStatus(renderStatus('Error: No account selected.', 'error'));
      return;
    }
    const el = document.getElementById('transaction-history');
    if (!el) {
      setReportStatus(renderStatus('Error: Transaction history table not found.', 'error'));
      return;
    }
    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>Transaction History – ${customerData.account_number}</title>
          <style>
            body {font-family: Inter, sans-serif;}
            table {width:100%;border-collapse:collapse;}
            th,td {padding:12px 15px;border:1px solid #ddd;text-align:left;}
            th {background:#f1f1f1;}
            .credit {background:#e8f5e9;}
            .debit {background:#fff0f0;}
          </style>
        </head>
        <body>
          <h2>Transaction History – ${customerData.first_name} ${customerData.last_name} (${customerData.account_number})</h2>
          ${el.outerHTML}
        </body>
      </html>
    `);
    win.document.close();
    win.print();
  };

  // ────── Transaction row click & render (unchanged) ──────
  const handleTransactionClick = tx => {
    setModalType(
      tx.transaction_type === 'INVOICE'
        ? 'invoice'
        : ['PAYMENT', 'CREDIT_NOTE'].includes(tx.transaction_type)
        ? 'payment'
        : null
    );
    setSelectedTx(tx);
  };

  const renderTransactionRow = tx => {
    const isCredit = +tx.amount < 0;
    const amount = Math.abs(+tx.amount).toFixed(2);
    const date = new Date(tx.transaction_date).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    const getIcon = () => {
      if (tx.transaction_type === 'INVOICE') {
        const inv = transactionStatuses.invoices.find(i => i.tx.transaction_id === tx.transaction_id);
        if (!inv) return 'Cross';
        const s = inv.remaining === 0 ? 'Check' : inv.remaining === +tx.amount ? 'Cross' : 'Warning';
        return <span style={{ color: s === 'Check' ? '#28a745' : s === 'Warning' ? '#ffc107' : '#dc3545' }}>{s}</span>;
      }
      if (tx.transaction_type === 'PAYMENT' || tx.transaction_type === 'CREDIT_NOTE') {
        const p = (tx.transaction_type === 'PAYMENT'
          ? transactionStatuses.payments
          : transactionStatuses.creditNotes
        ).find(i => i.tx.transaction_id === tx.transaction_id);
        if (!p) return 'Cross';
        const full = p.utilized >= Math.abs(+tx.amount);
        return <span style={{ color: full ? '#28a745' : '#dc3545' }}>{full ? 'Check' : 'Cross'}</span>;
      }
      return '';
    };

    return (
      <tr
        key={tx.transaction_id}
        style={{
          ...styles.tableRow,
          backgroundColor: isCredit ? '#e8f5e9' : '#fff0f0',
          cursor: 'pointer',
        }}
        onClick={() => handleTransactionClick(tx)}
      >
        <td style={styles.tableCell}>{date}</td>
        <td style={styles.tableCell}>
          <strong style={{ color: isCredit ? '#1b5e20' : '#b71c1c' }}>
            {isCredit ? 'CREDIT' : 'DEBIT'}
          </strong>
        </td>
        <td style={styles.tableCell}>{tx.description || tx.transaction_type}</td>
        <td style={styles.tableCell}>{tx.reference_number || '-'}</td>
        <td style={{ ...styles.tableCell, textAlign: 'right', fontWeight: 'bold', color: isCredit ? '#1b5e20' : '#b71c1c' }}>
          Ksh {amount}
        </td>
        <td style={styles.tableCell}>{getIcon()}</td>
        <td style={{ ...styles.tableCell, textAlign: 'right', fontWeight: 'bold' }}>
          Ksh {(+tx.running_balance).toFixed(2)}
        </td>
      </tr>
    );
  };

  // ────── Render modal (UPDATED: Billing Month) ──────
  const renderModal = () => {
    if (!selectedTx || !modalType) return null;

    const date = new Date(selectedTx.transaction_date).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    // NEW: Billing Month Logic
    const txDate = new Date(selectedTx.transaction_date);
    const day = txDate.getDate();
    const year = txDate.getFullYear();
    const month = txDate.getMonth();
    const billingMonth = day >= 20
      ? `${year}-${String(month + 1).padStart(2, '0')}`
      : `${year}-${String(month).padStart(2, '0')}`;

    let content;
    if (modalType === 'invoice') {
      const inv = transactionStatuses.invoices.find(i => i.tx.transaction_id === selectedTx.transaction_id);
      if (!inv) return null;

      let desc = selectedTx.description || selectedTx.transaction_type;
      if (selectedTx.reference_number) {
        if (selectedTx.reference_number.startsWith('RECON-')) desc = 'Reconnection Fee';
        else if (selectedTx.reference_number.startsWith('LABOR-')) desc = 'Labour Charge';
        else if (selectedTx.reference_number.startsWith('CONN-')) desc = 'Connection Fee';
        else if (selectedTx.reference_number.startsWith('MONTH-')) desc = 'Monthly Fee';
      }

      content = (
        <>
          <h3 style={styles.modalHeader}>Invoice Details</h3>
          <p><strong>Date:</strong> {date}</p>
          <p><strong>Billing Month:</strong> {billingMonth}</p>
          <p><strong>Description:</strong> {desc}</p>
          <p><strong>Amount:</strong> Ksh {inv.tx.amount.toFixed(2)}</p>
          <p><strong>Status:</strong> {inv.status}</p>
          {inv.applied.length > 0 && (
            <>
              <h4>Payments Applied:</h4>
              <ul style={{ listStyle: 'disc', paddingLeft: '20px' }}>
                {inv.applied.map((a, i) => (
                  <li key={i}>
                    Payment {a.payment.reference_number || a.payment.transaction_id}: Ksh {a.amount.toFixed(2)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      );
    } else if (modalType === 'payment') {
      const pay = transactionStatuses.payments.find(p => p.tx.transaction_id === selectedTx.transaction_id) ||
                 transactionStatuses.creditNotes.find(c => c.tx.transaction_id === selectedTx.transaction_id);
      if (!pay) return null;

      const allocs = customerData.allocations.filter(a => a.payment_transaction_id === selectedTx.transaction_id);

      content = (
        <>
          <h3 style={styles.modalHeader}>{selectedTx.transaction_type} Details</h3>
          <p><strong>Date:</strong> {date}</p>
          <p><strong>Amount:</strong> Ksh {Math.abs(pay.tx.amount).toFixed(2)}</p>
          <p><strong>Reference:</strong> {selectedTx.reference_number || '-'}</p>
          <p><strong>Status:</strong> {pay.status}</p>
          {allocs.length > 0 && (
            <>
              <h4>Allocated to Invoices:</h4>
              <ul style={{ listStyle: 'disc', paddingLeft: '20px' }}>
                {allocs.map((a, i) => {
                  const invTx = customerData.transactions.find(t => t.transaction_id === a.invoice_transaction_id);
                  let invDesc = invTx.description || invTx.transaction_type;
                  if (invTx.reference_number) {
                    if (invTx.reference_number.startsWith('RECON-')) invDesc = 'Reconnection Fee';
                    else if (invTx.reference_number.startsWith('LABOR-')) invDesc = 'Labour Charge';
                    else if (invTx.reference_number.startsWith('CONN-')) invDesc = 'Connection Fee';
                    else if (invTx.reference_number.startsWith('MONTH-')) invDesc = 'Monthly Fee';
                  }
                  return (
                    <li key={i}>
                      Invoice {invTx.reference_number || invTx.transaction_id} ({invDesc}): Ksh {(+a.amount).toFixed(2)}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </>
      );
    }

    return (
      <div style={styles.modalOverlay}>
        <div style={styles.modalContent}>
          {content}
          <button style={styles.closeButton} onClick={() => { setSelectedTx(null); setModalType(null); }}>
            Close
          </button>
        </div>
      </div>
    );
  };

  // ────── UI ──────
  const filterOptions = {
    NONE: 'No Filter (All Balances)',
    GTE: 'Greater Than or Equal To (>=)',
    GT: 'Greater Than (>)',
    LTE: 'Less Than or Equal To (<=)',
    LT: 'Less Than (<)',
  };

  return (
    <div style={styles.container}>
<div style={{ marginBottom: '20px' }}>
  <h2 style={styles.header}>Customer Balances & Transactions</h2>

  {/* MONTHLY BILLING BUTTON */}
  <div style={{ textAlign: 'center', margin: '20px 0' }}>
    <button
      onClick={handleRunMonthlyBilling}
      disabled={monthlyBillingStatus === 'running'}
      style={{
        padding: '14px 32px',
        fontSize: '18px',
        fontWeight: 'bold',
        backgroundColor: monthlyBillingStatus === 'running' ? '#ccc' : '#28a745',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: monthlyBillingStatus === 'running' ? 'not-allowed' : 'pointer',
        boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
        transition: 'all 0.3s'
      }}
    >
      {monthlyBillingStatus === 'running' ? 'Running Monthly Billing...' :
       monthlyBillingStatus === 'success' ? 'Monthly Billing Completed' :
       monthlyBillingStatus === 'error' ? 'Billing Failed – Try Again' :
       'Run Monthly Billing Now'}
    </button>

    {monthlyBillingStatus && (
      <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
        {monthlyBillingStatus === 'running' && 'Please wait — this may take a few seconds...'}
        {monthlyBillingStatus === 'success' && 'All customers have been billed successfully'}
        {monthlyBillingStatus === 'error' && 'Check server logs for details'}
      </div>
    )}
  </div>
</div>
      <div style={styles.dashboard}>
        {/* LEFT: Balances */}
        <div style={styles.balancesContainer}>
          <div style={styles.flexBetween}>
            <h3 style={{ ...styles.sectionHeader, borderBottom: 'none', marginBottom: 0 }}>
              Customers
            </h3>
            <button
              style={{ ...styles.submitButton, backgroundColor: '#007bff', marginTop: 0, padding: '8px 15px' }}
              onClick={() => setIsCustomerModalOpen(true)}
            >
              Bulk Upload Customers
            </button>
          </div>

          {/* ---------- DEFAULT FILTER (always visible) ---------- */}
<div style={styles.inputGroup}>
  <label htmlFor="statusFilter" style={styles.label}>Filter by Status</label>
  <select
    id="statusFilter"
    value={statusFilter}
    onChange={e => setStatusFilter(e.target.value)}
    style={styles.input}
  >
    <option value="All">All Users</option>
    <option value="Active">Active Users</option>
    <option value="Inactive">Inactive Users</option>
  </select>
</div>

{/* ---------- ADD FILTERS PANEL ---------- */}
<div style={{ marginBottom: '20px' }}>
  <button
    type="button"
    onClick={() => setExtraFiltersOpen(!extraFiltersOpen)}
    style={{
      ...styles.submitButton,
      backgroundColor: extraFiltersOpen ? '#dc3545' : '#28a745',
      padding: '8px 12px',
      fontSize: '0.9rem',
    }}
  >
    {extraFiltersOpen ? 'Hide Filters' : 'Add Filters'}
  </button>

  {extraFiltersOpen && (
    <div style={{
      marginTop: '15px',
      padding: '15px',
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      border: '1px solid #dee2e6',
    }}>
      {/* ---- Search ---- */}
      <div style={styles.inputGroup}>
        <label htmlFor="searchQuery" style={styles.label}>Search by Account Number or Name</label>
        <input
          id="searchQuery"
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Enter account number or name..."
          style={styles.input}
        />
      </div>

      {/* ---- Block ---- */}
      <div style={styles.inputGroup}>
        <label htmlFor="blockFilter" style={styles.label}>Filter by Block</label>
        <select
          id="blockFilter"
          value={blockFilter}
          onChange={e => setBlockFilter(e.target.value)}
          style={styles.input}
        >
          <option value="All">All Blocks</option>
          {blocks.map(b => (
            <option key={b.block_id} value={b.block_id}>{b.block_name}</option>
          ))}
        </select>
      </div>

      {/* ---- Membership ---- */}
      <div style={styles.inputGroup}>
        <label htmlFor="membershipFilter" style={styles.label}>Filter by Membership</label>
        <select
          id="membershipFilter"
          value={membershipFilter}
          onChange={e => setMembershipFilter(e.target.value)}
          style={styles.input}
        >
          <option value="All">All Memberships</option>
          <option value="Member">Members (M#)</option>
          <option value="Non-Member">Non-Members (N#)</option>
        </select>
      </div>

      {/* ---- Balance ---- */}
      <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label htmlFor="balance-filter-type" style={styles.label}>Balance Filter Type</label>
          <select
            id="balance-filter-type"
            value={balanceFilterType}
            onChange={e => setBalanceFilterType(e.target.value)}
            style={styles.input}
          >
            {Object.entries(filterOptions).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: 1 }}>
          <label htmlFor="min-balance-threshold" style={styles.label}>Threshold</label>
          <input
            id="min-balance-threshold"
            type="number"
            value={minBalanceThreshold}
            onChange={e => setMinBalanceThreshold(e.target.value)}
            disabled={balanceFilterType === 'NONE'}
            placeholder="Enter amount..."
            style={styles.input}
          />
        </div>

        <button
          onClick={handlePrintBalances}
          style={{
            ...styles.submitButton,
            backgroundColor: '#17a2b8',
            padding: '10px 15px',
          }}
        >
          Print Filtered Balances
        </button>
      </div>
    </div>
  )}
</div>

          {/* Balances table */}
          <div style={{ ...styles.tableWrapper, maxHeight: '500px', overflowY: 'auto' }}>
            <table style={styles.table} id="balances-table">
              <thead>
                <tr style={styles.tableHeaderRow}>
                  <th style={{ ...styles.tableHeader, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f1f1f1' }}>
                    Account Number
                  </th>
                  <th style={{ ...styles.tableHeader, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f1f1f1' }}>
                    Customer Name
                  </th>
                  <th style={{ ...styles.tableHeader, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f1f1f1' }}>
                    Block
                  </th>
                  <th style={{ ...styles.tableHeader, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f1f1f1' }}>
                    Balance (Ksh)
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredBalances.length ? (
                  filteredBalances.map(a => (
                    <tr
                      key={a.account_id}
                      style={{
                        ...styles.tableRow,
                        cursor: 'pointer',
                        backgroundColor:
                          selectedAccount === a.account_number ? '#e0f7fa' : 'transparent',
                      }}
                      onClick={() => handleAccountSelect(a.account_number)}
                    >
                      <td style={styles.tableCell}>{a.account_number}</td>
                      <td style={styles.tableCell}>{a.first_name} {a.last_name}</td>
                      <td style={styles.tableCell}>{a.block_name}</td>
                      <td
                        style={{
                          ...styles.tableCell,
                          textAlign: 'right',
                          color: a.current_balance < 0 ? '#1b5e20' : '#b71c1c',
                        }}
                      >
                        Ksh {(+a.current_balance).toFixed(2)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} style={styles.noHistory}>
                      No accounts match the search criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* RIGHT: Details / History */}
        <div style={styles.detailsContainer}>
          {customerData ? (
            <>
              <h3 style={styles.sectionHeader}>
                {viewMode === 'history' ? 'Transaction History' : 'Customer Details'} for{' '}
                {customerData.first_name} {customerData.last_name} ({customerData.account_number})
              </h3>

              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button
                  style={{
                    ...styles.submitButton,
                    backgroundColor: viewMode === 'details' ? '#007bff' : '#6c757d',
                    marginTop: 0,
                  }}
                  onClick={() => setViewMode('details')}
                >
                  Customer Details
                </button>
                <button
                  style={{
                    ...styles.submitButton,
                    backgroundColor: viewMode === 'history' ? '#007bff' : '#6c757d',
                    marginTop: 0,
                  }}
                  onClick={() => setViewMode('history')}
                >
                  Transaction History
                </button>
              </div>

              {viewMode === 'history' ? (
                <>
                  <div style={styles.flexBetween}>
                    <div style={styles.inputGroup}>
                      <label htmlFor="transactionFilter" style={styles.label}>
                        Filter Transactions
                      </label>
                      <select
                        id="transactionFilter"
                        value={transactionFilter}
                        onChange={e => setTransactionFilter(e.target.value)}
                        style={styles.input}
                      >
                        <option value="All">All Transactions</option>
                        <option value="INVOICE">Invoices</option>
                        <option value="PAYMENT">Payments</option>
                        <option value="CREDIT_NOTE">Credit Notes</option>
                      </select>
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        style={{
                          ...styles.submitButton,
                          backgroundColor: '#8a2be2',
                          padding: '8px 15px',
                          marginTop: '10px',
                        }}
                        onClick={() => setIsTransactionModalOpen(true)}
                      >
                        Bulk Upload Transactions
                      </button>

                      {/* NEW: Create Invoice Button */}
                      <button
                        style={{
                          ...styles.submitButton,
                          backgroundColor: '#ff851b',
                          padding: '8px 15px',
                          marginTop: '10px',
                        }}
                        onClick={() => setIsCreateInvoiceOpen(true)}
                      >
                        Create Invoice
                      </button>
                    </div>
                  </div>

                  <button style={{ ...styles.submitButton, backgroundColor: '#17a2b8' }} onClick={handlePrintHistory}>
                    Print Transaction History
                  </button>

                  {reportStatus}

                  {transactionStatuses.ledger.length ? (
                    <div style={{ ...styles.tableWrapper, maxHeight: '540px', overflowY: 'auto' }}>
                      <table style={styles.table} id="transaction-history">
                        <thead>
                          <tr style={styles.tableHeaderRow}>
                            <th style={{ ...styles.tableHeader, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f1f1f1' }}>
                              Date
                            </th>
                            <th style={{ ...styles.tableHeader, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f1f1f1' }}>
                              Type
                            </th>
                            <th style={{ ...styles.tableHeader, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f1f1f1' }}>
                              Description
                            </th>
                            <th style={{ ...styles.tableHeader, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f1f1f1' }}>
                              Ref No.
                            </th>
                            <th
                              style={{
                                ...styles.tableHeader,
                                textAlign: 'right',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                backgroundColor: '#f1f1f1',
                              }}
                            >
                              Amount (Ksh)
                            </th>
                            <th style={{ ...styles.tableHeader, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f1f1f1' }}>
                              Status
                            </th>
                            <th
                              style={{
                                ...styles.tableHeader,
                                textAlign: 'right',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                backgroundColor: '#f1f1f1',
                              }}
                            >
                              Running Balance (Ksh)
                            </th>
                          </tr>
                        </thead>
                        <tbody>{transactionStatuses.ledger.map(renderTransactionRow)}</tbody>
                      </table>
                    </div>
                  ) : (
                    <p style={styles.noHistory}>No transactions found for this filter.</p>
                  )}
                </>
              ) : (
                <>
                  {editingCustomer && (
                    <div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>First Name</label>
                        <input
                          type="text"
                          value={editingCustomer.first_name}
                          onChange={e => setEditingCustomer({ ...editingCustomer, first_name: e.target.value })}
                          style={styles.input}
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Last Name</label>
                        <input
                          type="text"
                          value={editingCustomer.last_name}
                          onChange={e => setEditingCustomer({ ...editingCustomer, last_name: e.target.value })}
                          style={styles.input}
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Phone Number</label>
                        <input
                          type="text"
                          value={editingCustomer.phone_number}
                          onChange={e => setEditingCustomer({ ...editingCustomer, phone_number: e.target.value })}
                          style={styles.input}
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Monthly Rate (Ksh)</label>
                        <input
                          type="number"
                          value={editingCustomer.monthly_rate}
                          onChange={e => setEditingCustomer({ ...editingCustomer, monthly_rate: e.target.value })}
                          style={styles.input}
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Next Billing Date</label>
                        <input
                          type="date"
                          value={editingCustomer.next_billing_date || ''}
                          readOnly
                          style={{ ...styles.input, backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                        />
                        <small style={{ color: '#007bff', fontSize: '0.8em', fontWeight: '600' }}>
                          Set by the system – no manual override
                        </small>
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Installation Address</label>
                        <input
                          type="text"
                          value={editingCustomer.installation_address}
                          onChange={e => setEditingCustomer({ ...editingCustomer, installation_address: e.target.value })}
                          style={styles.input}
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Block</label>
                        <select
                          value={editingCustomer.block_id}
                          onChange={e => setEditingCustomer({ ...editingCustomer, block_id: e.target.value })}
                          style={styles.input}
                        >
                          {blocks.map(b => (
                            <option key={b.block_id} value={b.block_id}>
                              {b.block_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>
                          <input
                            type="checkbox"
                            checked={editingCustomer.is_active}
                            onChange={e => setEditingCustomer({ ...editingCustomer, is_active: e.target.checked })}
                          />{' '}
                          Active
                        </label>
                      </div>

                      <button style={{ ...styles.submitButton, backgroundColor: '#28a745' }} onClick={handleUpdateCustomer}>
                        Save Changes
                      </button>

                      {reportStatus}
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <div style={styles.placeholder}>
              <h3>Select an account to view transaction history</h3>
            </div>
          )}
        </div>
      </div>

      {renderModal()}

      <BulkUploadModal
        isOpen={isCustomerModalOpen}
        onClose={() => setIsCustomerModalOpen(false)}
        type="Customer Information"
        headers={CUSTOMER_HEADERS}
        instructions="Please fill the downloaded template with new or updated customer data. Do not change the column headers. The Account Number is essential for identifying existing records. For 'Is Active', use 'true' or 'false'. Use YYYY-MM-DD for Next Billing Date."
        onUpload={handleCustomerBulkUpload}
        styles={styles}
      />

      <BulkUploadModal
        isOpen={isTransactionModalOpen}
        onClose={() => setIsTransactionModalOpen(false)}
        type="Transactions"
        headers={TRANSACTION_HEADERS}
        instructions="Fill the downloaded template with customer transactions. Use NEGATIVE amounts for Payments/Credit Notes and POSITIVE amounts for Invoices. The system will automatically generate Status and Running Balance columns; do NOT include them in your upload file."
        onUpload={handleTransactionBulkUpload}
        styles={styles}
      />

      {/* NEW: Create Invoice Modal */}
      {isCreateInvoiceOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.bulkModalContent}>
            <h3 style={styles.modalHeader}>Create Manual Invoice</h3>
            <form onSubmit={handleCreateInvoice}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Amount (Ksh)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={invoiceForm.amount}
                  onChange={e => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
                  style={styles.input}
                  placeholder="e.g. 1500.00"
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Description (Optional)</label>
                <input
                  type="text"
                  value={invoiceForm.description}
                  onChange={e => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                  style={styles.input}
                  placeholder="e.g. Reconnection Fee"
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Reference Number (Optional)</label>
                <input
                  type="text"
                  value={invoiceForm.referenceNumber}
                  onChange={e => setInvoiceForm({ ...invoiceForm, referenceNumber: e.target.value })}
                  style={styles.input}
                  placeholder="e.g. RECON-001"
                />
              </div>
              <div style={styles.inputGroup}>
                    <label style={styles.label}>Invoice Month (From)</label>
                <input
  type="month"
  value={invoiceForm.billFromMonth}
  onChange={(e) =>
    setInvoiceForm({ ...invoiceForm, billFromMonth: e.target.value })
  }
                  style={styles.input}
                />
              </div>
              <div style={styles.modalFooter}>
                <button
                  type="button"
                  style={{ ...styles.submitButton, backgroundColor: '#6c757d', marginRight: '10px' }}
                  onClick={() => setIsCreateInvoiceOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ ...styles.submitButton, backgroundColor: '#28a745' }}
                >
                  Create Invoice
                </button>
              </div>
            </form>
            <button onClick={() => setIsCreateInvoiceOpen(false)} style={styles.closeButtonSmall}>
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// STYLES (unchanged)
// ──────────────────────────────────────────────────────────────
const styles = {
  container: {
    maxWidth: '1200px',
    margin: '40px auto',
    padding: '30px',
    backgroundColor: '#f9f9f9',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    fontFamily: 'Inter, sans-serif',
  },
  header: {
    textAlign: 'center',
    color: '#007bff',
    marginBottom: '25px',
    fontSize: '1.8em',
    borderBottom: '2px solid #eef2f5',
    paddingBottom: '10px',
  },
  dashboard: { display: 'flex', gap: '20px', marginBottom: '30px' },
  balancesContainer: {
    flex: '1',
    border: '1px solid #ddd',
    padding: '20px',
    borderRadius: '8px',
    backgroundColor: '#fff',
    boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
  },
  detailsContainer: {
    flex: '2',
    border: '1px solid #ddd',
    padding: '20px',
    borderRadius: '8px',
    backgroundColor: '#fff',
    boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
  },
  sectionHeader: {
    color: '#333',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px',
    marginBottom: '15px',
    fontSize: '1.4em',
    fontWeight: '700',
  },
  tableWrapper: { overflowX: 'auto', borderRadius: '8px', border: '1px solid #ddd' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.95em' },
  tableHeaderRow: { backgroundColor: '#f1f1f1' },
  tableHeader: {
    padding: '12px 15px',
    textAlign: 'left',
    fontWeight: '700',
    color: '#555',
    borderBottom: '2px solid #ddd',
  },
  tableCell: { padding: '12px 15px', borderBottom: '1px solid #eee', verticalAlign: 'top' },
  tableRow: { transition: 'background-color 0.2s' },
  placeholder: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    color: '#777',
    fontSize: '1.2em',
  },
  noHistory: {
    textAlign: 'center',
    padding: '20px',
    color: '#777',
    backgroundColor: '#f5f5f5',
    borderRadius: '6px',
  },
  inputGroup: { marginBottom: '15px' },
  label: { display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' },
  input: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    boxSizing: 'border-box',
    fontSize: '16px',
    transition: 'border-color 0.3s',
  },
  submitButton: {
    width: 'auto',
    padding: '12px 20px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'background-color 0.3s',
    marginTop: '15px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    width: '400px',
    maxHeight: '80%',
    overflowY: 'auto',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  bulkModalContent: {
    backgroundColor: '#fff',
    padding: '30px',
    borderRadius: '8px',
    width: '500px',
    maxHeight: '90%',
    overflowY: 'auto',
    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    position: 'relative',
  },
  modalHeader: {
    marginBottom: '20px',
    fontSize: '1.5em',
    fontWeight: '700',
    color: '#007bff',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px',
  },
  closeButton: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    marginTop: '15px',
  },
  closeButtonSmall: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '1.5em',
    color: '#6c757d',
    cursor: 'pointer',
  },
  flexBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
  },
  instructionsText: {
    marginBottom: '20px',
    padding: '10px',
    backgroundColor: '#f8f9fa',
    borderLeft: '4px solid #007bff',
    borderRadius: '4px',
    fontSize: '0.9em',
    lineHeight: '1.4',
  },
  downloadSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px',
    backgroundColor: '#e9ecef',
    borderRadius: '6px',
    marginBottom: '15px',
  },
  downloadText: { fontWeight: '600', color: '#333' },
  downloadButton: {
    padding: '8px 15px',
    backgroundColor: '#28a745',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '4px',
    fontWeight: 'bold',
    transition: 'background-color 0.3s',
    display: 'inline-block',
  },
  uploadSection: {
    padding: '15px',
    backgroundColor: '#f1f1f1',
    borderRadius: '6px',
    marginBottom: '20px',
  },
  fileInput: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    boxSizing: 'border-box',
    fontSize: '16px',
    marginTop: '10px',
    backgroundColor: '#fff',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: '10px',
    borderTop: '1px solid #eee',
  },
};

export default CustomerBalances;